import { Dropdown, Button } from 'react-bootstrap';
import { Download, Upload } from 'lucide-react';

import { OrderDateRangePicker } from './OrderDateRangePicker';
import { CountryFilter } from './CountryFilter';
import { OrderStatusFilter } from './OrderStatusFilter';
import { PageSizeSelector } from './PageSizeSelector';
import { SearchFieldSelect } from './SearchFieldSelect';
import { SourcingInfoFilter } from './SourcingInfoFilter';
import type { Column } from './Columns';

export interface OrdersTableFilterControlProps {
  allColumns: Column[];
  visibleColumns: Column[];
  onColumnVisibilityChange: (column: Column, isVisible: boolean) => void;
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  country: string;
  onCountryChange: (country: string) => void;
  status: string;
  onStatusChange: (status: string) => void;
  itemsPerPage: string;
  onItemsPerPageChange: (value: string) => void;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  onSearchSubmit: () => void;
  onSearchClear: () => void;
  picValue: string;
  onPicValueChange: (value: string) => void;
  onPicSubmit: () => void;
  onPicClear: () => void;
  hasOrderNumber: boolean;
  onHasOrderNumberChange: (value: boolean) => void;
  hasTrackingNumber: boolean;
  onHasTrackingNumberChange: (value: boolean) => void;
  onOpenParxlModal?: () => void;
  onOpenYunModal?: () => void;
  onOpenImportModal?: () => void;
}

export function OrdersTableFilterControl({
  allColumns,
  visibleColumns,
  onColumnVisibilityChange,
  startDate,
  endDate,
  onDateRangeChange,
  country,
  onCountryChange,
  status,
  onStatusChange,
  itemsPerPage,
  onItemsPerPageChange,
  searchValue,
  onSearchValueChange,
  onSearchSubmit,
  onSearchClear,
  picValue,
  onPicValueChange,
  onPicSubmit,
  onPicClear,
  hasOrderNumber,
  onHasOrderNumberChange,
  hasTrackingNumber,
  onHasTrackingNumberChange,
  onOpenParxlModal,
  onOpenYunModal,
  onOpenImportModal,
}: OrdersTableFilterControlProps) {
  return (
    <div className="d-flex align-items-center gap-2 flex-wrap w-full">
      <OrderDateRangePicker startDate={startDate} endDate={endDate} onDateRangeChange={onDateRangeChange} />

      <SearchFieldSelect
        value={searchValue}
        onValueChange={onSearchValueChange}
        onSubmit={onSearchSubmit}
        onClear={onSearchClear}
      />

      <SearchFieldSelect
        value={picValue}
        onValueChange={onPicValueChange}
        onSubmit={onPicSubmit}
        onClear={onPicClear}
        placeholder="PIC..."
        width="140px"
      />

      <OrderStatusFilter status={status} onStatusChange={onStatusChange} />

      <SourcingInfoFilter
        hasOrderNumber={hasOrderNumber}
        onHasOrderNumberChange={onHasOrderNumberChange}
        hasTrackingNumber={hasTrackingNumber}
        onHasTrackingNumberChange={onHasTrackingNumberChange}
      />

      <CountryFilter country={country} onCountryChange={onCountryChange} />

      <PageSizeSelector itemsPerPage={itemsPerPage} onItemsPerPageChange={onItemsPerPageChange} />

      <Dropdown>
        <Dropdown.Toggle variant="outline-dark" className="bg-gray-50 hover:bg-gray-100">
          Columns
        </Dropdown.Toggle>
        <Dropdown.Menu className="p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {allColumns.map((column) => (
            <div key={String(column.accessor)} className="d-flex align-items-center gap-2 p-1">
              <input
                type="checkbox"
                checked={visibleColumns.some((col) => col.accessor === column.accessor)}
                onChange={(e) => onColumnVisibilityChange(column, e.target.checked)}
                disabled={column.isRequired}
              />
              <span className={column.isRequired ? 'text-gray-400' : ''}>{column.header}</span>
            </div>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {onOpenParxlModal && (
        <Button variant="primary" onClick={onOpenParxlModal} className="d-flex align-items-center gap-2">
          <Download size={16} />
          Export
        </Button>
      )}

      {onOpenYunModal && (
        <Button variant="success" onClick={onOpenYunModal} className="d-flex align-items-center gap-2">
          <Download size={16} />
          Yun Export
        </Button>
      )}

      {onOpenImportModal && (
        <Button variant="outline-primary" onClick={onOpenImportModal} className="d-flex align-items-center gap-2">
          <Upload size={16} />
          Import
        </Button>
      )}
    </div>
  );
}
